import { StringData } from "../..";
import { ISettingsChangeData } from "../../models/event/ISettingsChangeData";
import { IStringModule } from "../IStringModule";
import { StringModule } from "../StringModule";

type LifecycleName =
  | "destroy"
  | "onInit"
  | "onFrame"
  | "onMutate"
  | "onScrollMeasure"
  | "onMouseMoveMeasure"
  | "onScroll"
  | "onResizeWidth"
  | "onResize"
  | "onMouseMove"
  | "onWheel"
  | "onDirectionChange"
  | "onScrollStart"
  | "onScrollStop"
  | "onAxisChange"
  | "onDeviceChange"
  | "onScrollConfigChange"
  | "onSettingsChange"
  | "onDOMMutate";

function toLifecycleArgs(
  lifecycle: LifecycleName,
  data: StringData,
  arg?: unknown,
  arg2?: unknown
): unknown[] {
  switch (lifecycle) {
    case "onFrame":
    case "onMutate":
    case "onScrollMeasure":
    case "onMouseMoveMeasure":
    case "onScroll":
      return [data];
    case "onMouseMove":
    case "onWheel":
      return arg ? [arg] : [];
    case "onDOMMutate":
      return arg && arg2 ? [arg, arg2] : [];
    case "onSettingsChange":
      return [];
    default:
      return [];
  }
}

export class ModuleManager {
  private modules: StringModule[] = [];
  private uiModules: StringModule[] = [];
  private allModules: StringModule[] = [];

  constructor(private data: StringData) {}

  register(module: StringModule): void {
    if (module.type === 1) {
      this.modules.push(module);
    } else if (module.type === 2) {
      this.uiModules.push(module);
    }

    this.rebuildAllModules();
  }

  find<T>(type: new (...args: any[]) => T): T | undefined {
    return this.modules.find((m) => m instanceof type) as T | undefined;
  }

  onInit(): void {
    this.callAll("onInit");
  }

  onFrame(): void {
    this.callAll("onFrame");
  }

  onMutate(): void {
    this.callAll("onMutate");
  }

  onScrollMeasure(): void {
    this.callAll("onScrollMeasure");
  }

  onMouseMoveMeasure(): void {
    this.callAll("onMouseMoveMeasure");
  }

  onScroll(): void {
    this.callAll("onScroll");
  }

  onResizeWidth(): void {
    this.callAll("onResizeWidth");
  }

  onResize(): void {
    this.callAll("onResize");
  }

  onMouseMove(e: MouseEvent): void {
    this.callAll("onMouseMove", e);
  }

  onWheel(e: WheelEvent): void {
    this.callAll("onWheel", e);
  }

  onDirectionChange(): void {
    this.callAll("onDirectionChange");
  }

  onScrollStart(): void {
    this.callAll("onScrollStart");
  }

  onScrollStop(): void {
    this.callAll("onScrollStop");
  }

  onAxisChange(): void {
    this.callAll("onAxisChange");
  }

  onDeviceChange(): void {
    this.callAll("onDeviceChange");
  }

  onScrollConfigChange(): void {
    this.callAll("onScrollConfigChange");
  }

  onSettingsChange(_data: ISettingsChangeData): void {
    this.callAll("onSettingsChange");
  }

  onDOMMutate(added: NodeList, removed: NodeList): void {
    this.callAll("onDOMMutate", added, removed);
  }

  destroy(): void {
    this.callAll("destroy");
    this.modules = [];
    this.uiModules = [];
    this.allModules = [];
  }

  get all(): IStringModule[] {
    return this.allModules;
  }

  get core(): IStringModule[] {
    return this.modules;
  }

  get ui(): IStringModule[] {
    return this.uiModules;
  }

  private callAll(lifecycle: LifecycleName, arg?: unknown, arg2?: unknown): void {
    this.callLifecycle(this.modules, lifecycle, arg, arg2);
    this.callLifecycle(this.uiModules, lifecycle, arg, arg2);
  }

  private callLifecycle(
    modules: StringModule[],
    lifecycle: LifecycleName,
    arg?: unknown,
    arg2?: unknown
  ): void {
    if (modules.length === 0) {
      return;
    }

    const args = toLifecycleArgs(lifecycle, this.data, arg, arg2);

    for (let i = 0; i < modules.length; i++) {
      const module = modules[i];
      if (!module) continue;

      (module as any)[lifecycle](...args);
    }
  }

  private rebuildAllModules(): void {
    this.allModules = [...this.modules, ...this.uiModules];
  }
}
